import React from "react";
import { connect, ConnectedProps } from "react-redux";
import { Dropdown, Header, Image, Menu, Icon } from "semantic-ui-react";
import { getSystemsFromUserRoles, logout, renewToken } from "../../actions/users/users";
import logo from "../../assets/images/urbansim/logo.png";
import { amRoutes, externalLinks, pencilerRoles } from "../../config/config";
import { AppDispatch, RootState } from "../../reducers";
import { history } from "../../store";
import { resetApp } from "../../store/appStatus";
import { setIsLoading } from "../../store/statusMessages";
import { Role } from "../../types/roles";
import { exists, isAdministrator, isPublic } from "../../utils/utils";
import About from "../../views/About";
import RegionSelector from "./RegionSelector";

const mapStateToProps = (state: RootState) => {
  return {
    user: state.users.userClaims,
    availableRoles: state.users.availableRoles
  };
};

const mapDispatchToProps = (dispatch: AppDispatch) => ({
  renewToken: (values: { regionId?: number; organizationId?: number }) =>
    dispatch(renewToken(values)),
  logout: (redirectToUrl: string | null) => dispatch(logout(redirectToUrl)),
  resetApp: () => dispatch(resetApp()),
  setIsLoading: (isLoading: boolean) => dispatch(setIsLoading(isLoading))
});

const connector = connect(mapStateToProps, mapDispatchToProps);
type PropsFromRedux = ConnectedProps<typeof connector>;
type HeaderComponentProps = {} & PropsFromRedux;
type HeaderComponentState = {
  openAbout: boolean;
  redirectTo: string;
};

class HeaderComponent extends React.Component<HeaderComponentProps, HeaderComponentState> {
  constructor(props: HeaderComponentProps) {
    super(props);
    this.state = {
      openAbout: false,
      redirectTo: document.referrer
    };
  }

  handleOpenAboutModal = () => this.setState({ openAbout: true });
  handleCloseAboutModal = () =>
    this.setState({
      openAbout: false
    });

  handleLogOutClick(redirectToURL: string | null) {
    this.props.logout(redirectToURL);
    this.props.resetApp();
    this.props.setIsLoading(true);
  }

  createURLIfValid(string: string) {
    try {
      return new URL(string);
    } catch (_) {
      return null;
    }
  }

  render() {
    const { metadata, selectedRoles } = this.props.user;
    let redirectButton;
    let ucLink: string | undefined | null = externalLinks.urbancanvas;

    let redirectToURL: URL | string | undefined | null = this.createURLIfValid(
      this.state.redirectTo
    );
    if (exists(metadata) && metadata.links !== undefined) {
      const {
        links: { frontendUrl }
      } = metadata;
      ucLink = frontendUrl;
      redirectToURL = frontendUrl;
    }

    if (exists(redirectToURL)) {
      redirectButton = (
        <Menu.Item name="backTo" onClick={() => (window.location.href = redirectToURL as string)}>
          <Icon name="map" />
          Back to Map
        </Menu.Item>
      );
    }

    const selectedRole = selectedRoles && selectedRoles.length > 0 ? selectedRoles[0] : null;
    return (
      <div>
        <Menu>
          <Menu.Item name="headerMenuLog">
            <Header as="h5" textAlign="center">
              <Image src={logo} size="small" aria-label="Urbancanvas logo" /> UrbanSim Account
              Manager
            </Header>
          </Menu.Item>
          {redirectButton}
          <Menu.Menu position="right">
            {selectedRole !== null && this.props.availableRoles !== null && (
              <RegionSelector
                selectedRole={selectedRole}
                availableRoles={this.props.availableRoles}
                onSubmit={(role: Role) =>
                  this.props.renewToken({
                    regionId: role.region?.id,
                    organizationId: role.organization?.id
                  })
                }
                isPencilerUser={getSystemsFromUserRoles(selectedRoles ?? []).includes(
                  pencilerRoles.system
                )}
              />
            )}
            <Dropdown
              aria-label="Navigation dropdown menu"
              floating
              fluid
              text={`${this.props.user.user}`}
              style={{
                margin: "auto",
                display: "flex",
                padding: "20px 17px",
                borderLeft: "1px solid hsla(0,0%,83%,.7)"
              }}
              icon={
                <Icon
                  name="dropdown"
                  style={{
                    margin: "auto",
                    marginLeft: "4px"
                  }}
                />
              }
            >
              <Dropdown.Menu>
                <Dropdown
                  text="Manage Settings"
                  icon={false}
                  item
                  simple
                  aria-label="Settings dropdown menu"
                >
                  <Dropdown.Menu style={{ position: "absolute", right: "100%" }}>
                    <Dropdown.Item
                      text="Your Profile"
                      onClick={() => history.push(amRoutes.profile)}
                    />
                    {!isPublic(this.props.user) && (
                      <Dropdown.Item
                        text="Your Subscription"
                        onClick={() => history.push(amRoutes.management)}
                      />
                    )}
                    {isAdministrator(this.props.user) && (
                      <Dropdown.Item
                        text="Your Account Users"
                        onClick={() => history.push(amRoutes.managementUsers)}
                      />
                    )}
                    {!isPublic(this.props.user) && (
                      <Dropdown.Item
                        text="Your Permissions"
                        onClick={() => history.push(amRoutes.managementPermissions)}
                      />
                    )}
                    <Dropdown.Item
                      text="Your Regions"
                      onClick={() => history.push(amRoutes.managementRegions)}
                    />
                    <Dropdown.Item
                      text="Your Settings"
                      onClick={() => history.push(amRoutes.managementSettings)}
                    />
                    {!isPublic(this.props.user) && (
                      <Dropdown.Item
                        text="Your Account Security"
                        onClick={() => history.push(amRoutes.managementSecurity)}
                      />
                    )}
                  </Dropdown.Menu>
                </Dropdown>
                <Dropdown text="Help" icon={false} item simple aria-label="Help dropdown menu">
                  <Dropdown.Menu style={{ position: "absolute", right: "100%" }}>
                    <Dropdown.Item
                      as="a"
                      href={`${externalLinks.urbancanvas}docs`}
                      target="_blank"
                      text="Documentation"
                    />
                    <Dropdown.Item
                      as="a"
                      href={externalLinks.support}
                      target="_blank"
                      text="Send feedback"
                    />
                    <Dropdown.Item
                      as="a"
                      href={externalLinks.support}
                      target="_blank"
                      text="Report a problem"
                    />
                  </Dropdown.Menu>
                </Dropdown>
                <Dropdown.Divider />
                <Dropdown.Item
                  onClick={this.handleOpenAboutModal}
                  content={
                    <About open={this.state.openAbout} onClose={this.handleCloseAboutModal} />
                  }
                />
                <Dropdown.Item text="Penciler" as="a" href={externalLinks.penciler} />
                {exists(redirectToURL) && <Dropdown.Item text="UrbanCanvas" as="a" href={ucLink} />}
                <Dropdown.Divider />
                <Dropdown.Item
                  text="Logout"
                  onClick={() => {
                    this.handleLogOutClick(exists(redirectToURL) ? `${redirectToURL}/login` : null);
                  }}
                />
              </Dropdown.Menu>
            </Dropdown>
          </Menu.Menu>
        </Menu>
      </div>
    );
  }
}

export default connector(HeaderComponent);
